import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, statusMeta } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Search, Trash2, Hammer, Wallet, TrendingUp, TrendingDown,
  Percent, ChevronRight, FileText, Building2, DollarSign, Receipt, PiggyBank,
  Pencil, FileBarChart2, MoreVertical, FileSignature
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

const TRADE_CATEGORIES = ["Excavation","Concrete","Framing","Plumbing","Electrical","Roofing","Landscaping","Painting","Cabinetry","Windows & Doors","Insulation","Tiling","Other"];

const emptyTrade = {
  name: "", category: "", supplier: "", description: "",
  original_estimate: 0, updated_estimate: "", quoted_amount: "",
  approved_variations: 0, status: "not_quoted", notes: "",
};

const emptyProject = { name: "", client_name: "", address: "", original_estimate: 0, notes: "" };

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [trades, setTrades] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [tradeDialog, setTradeDialog] = useState({ open: false, editingId: null, form: emptyTrade });
  const [projectDialog, setProjectDialog] = useState({ open: false, form: emptyProject });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, tRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/trades`),
      ]);
      setProject(pRes.data);
      setTrades(tRes.data);
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 404) nav("/projects", { replace: true });
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!trades) return null;
    return trades.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!q) return true;
      const term = q.toLowerCase();
      return [t.name, t.supplier, t.category].filter(Boolean).some((s) => s.toLowerCase().includes(term));
    });
  }, [trades, q, statusFilter]);

  function openEditProject() {
    setProjectDialog({
      open: true,
      form: {
        name: project.name || "",
        client_name: project.client_name || "",
        address: project.address || "",
        original_estimate: project.original_estimate || 0,
        notes: project.notes || "",
      },
    });
  }

  async function saveProject(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/projects/${id}`, {
        ...projectDialog.form,
        original_estimate: Number(projectDialog.form.original_estimate) || 0,
      });
      toast.success("Project updated");
      setProjectDialog({ open: false, form: emptyProject });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function deleteProject() {
    if (!window.confirm(`Delete project "${project.name}"? All trades, payments and files will be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      nav("/projects", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  function openNewTrade() {
    setTradeDialog({ open: true, editingId: null, form: emptyTrade });
  }

  function openEditTrade(t) {
    setTradeDialog({
      open: true,
      editingId: t.id,
      form: {
        name: t.name || "",
        category: t.category || "",
        supplier: t.supplier || "",
        description: t.description || "",
        original_estimate: t.original_estimate || 0,
        updated_estimate: t.updated_estimate ?? "",
        quoted_amount: t.quoted_amount ?? "",
        approved_variations: t.approved_variations || 0,
        status: t.status || "not_quoted",
        notes: t.notes || "",
      },
    });
  }

  async function submitTrade(e) {
    e.preventDefault();
    setSaving(true);
    const f = tradeDialog.form;
    const body = {
      ...f,
      original_estimate: Number(f.original_estimate) || 0,
      updated_estimate: f.updated_estimate === "" ? null : Number(f.updated_estimate),
      quoted_amount: f.quoted_amount === "" ? null : Number(f.quoted_amount),
      approved_variations: Number(f.approved_variations) || 0,
    };
    try {
      if (tradeDialog.editingId) {
        await api.put(`/trades/${tradeDialog.editingId}`, body);
        toast.success("Trade updated");
      } else {
        await api.post(`/projects/${id}/trades`, body);
        toast.success("Trade added");
      }
      setTradeDialog({ open: false, editingId: null, form: emptyTrade });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeTrade(tradeId) {
    if (!window.confirm("Delete this trade and all its payments?")) return;
    try {
      await api.delete(`/trades/${tradeId}`);
      toast.success("Trade deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  if (!project) return <div className="text-slate-500">Loading…</div>;
  const s = project.summary || {};
  const over = (s.over_under_budget || 0) < 0;

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/projects" className="text-slate-500 hover:text-brandblue inline-flex items-center gap-1" data-testid="link-back">
          <ArrowLeft className="h-4 w-4" /> Projects
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700 font-medium truncate">{project.name}</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> {project.client_name || "No client"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight truncate" data-testid="project-title">{project.name}</h1>
          {project.address && <p className="text-sm text-slate-500 mt-1.5 truncate">{project.address}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/projects/${id}/report`}>
            <Button variant="outline" className="h-11" data-testid="btn-view-report">
              <FileBarChart2 className="h-4 w-4" /> Report
            </Button>
          </Link>
          <Link to={`/projects/${id}/invoice/new`}>
            <Button className="h-11 bg-navy hover:bg-navy-hover text-white" data-testid="btn-create-invoice">
              <FileSignature className="h-4 w-4" /> New Invoice
            </Button>
          </Link>
          <Button onClick={openEditProject} variant="outline" className="h-11" data-testid="btn-edit-project">
            <Pencil className="h-4 w-4" /> Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-11 w-11" data-testid="btn-project-more"><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={openEditProject} data-testid="menu-edit-project"><Pencil className="h-4 w-4" /> Edit project</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={deleteProject} className="text-status-danger-text" data-testid="menu-delete-project">
                <Trash2 className="h-4 w-4" /> Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Dashboard */}
      <section>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Project dashboard</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger" data-testid="dashboard-grid">
          <Metric icon={<DollarSign />} label="Original estimate" value={fmtMoney(s.project_original_estimate)} />
          <Metric icon={<TrendingUp />} label="Current updated est." value={fmtMoney(s.current_updated_estimate)} />
          <Metric icon={<FileText />} label="Total quotes value" value={fmtMoney(s.total_quotes_value)} />
          <Metric icon={<Receipt />} label="Approved variations" value={fmtMoney(s.approved_variations)} />
          <Metric icon={<Wallet />} label="Total paid" value={fmtMoney(s.total_paid)} tone="paid" />
          <Metric icon={<PiggyBank />} label="Total owing" value={fmtMoney(s.total_owing)} tone="part" />
          <Metric icon={<TrendingUp />} label="Estimated final cost" value={fmtMoney(s.estimated_final_cost)} />
          <Metric
            icon={over ? <TrendingDown /> : <TrendingUp />}
            label={over ? "Over budget" : "Under budget"}
            value={fmtMoney(Math.abs(s.over_under_budget || 0))}
            tone={over ? "danger" : "paid"}
          />
        </div>

        <Card className="mt-4 p-5 border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Percent className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Budget used</span>
            </div>
            <span className="font-display font-bold text-lg text-slate-900" data-testid="budget-percent">{s.percent_budget_used || 0}%</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                over ? "bg-status-danger-text" : (s.percent_budget_used || 0) > 80 ? "bg-status-part-text" : "bg-status-paid-text"
              }`}
              style={{ width: `${Math.min(s.percent_budget_used || 0, 100)}%` }}
            />
          </div>
        </Card>
      </section>

      {/* Trades */}
      <section>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Trades & cost categories</div>
            <h2 className="font-display text-2xl font-semibold text-slate-900 mt-1">{trades?.length || 0} trades</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search trades…" className="h-11 pl-9 w-52 md:w-56" data-testid="trades-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 w-40" data-testid="trades-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries({
                  not_quoted: "Not Quoted", quoted: "Quoted", not_started: "Not Started",
                  part_paid: "Part Paid", paid: "Paid", over_budget: "Over Budget",
                }).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={openNewTrade} className="h-11 bg-brandblue hover:bg-brandblue-hover text-white px-5" data-testid="btn-add-trade">
              <Plus className="h-4 w-4" /> Add trade
            </Button>
          </div>
        </div>

        {filtered === null ? (
          <div className="h-40 bg-white border border-border rounded-lg animate-pulse" />
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-2" data-testid="trades-empty">
            <Hammer className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <h3 className="font-display text-lg font-semibold">No trades yet</h3>
            <p className="text-sm text-slate-500 mt-1">Add your first trade — excavation, framing, plumbing…</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 stagger" data-testid="trades-list">
            {filtered.map((t) => <TradeRow key={t.id} t={t} onEdit={() => openEditTrade(t)} onDelete={() => removeTrade(t.id)} />)}
          </div>
        )}
      </section>

      {/* Edit project dialog */}
      <Dialog open={projectDialog.open} onOpenChange={(o) => setProjectDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit project</DialogTitle></DialogHeader>
          <form onSubmit={saveProject} className="space-y-4" data-testid="edit-project-form">
            <Field label="Project name" required>
              <Input required value={projectDialog.form.name} onChange={(e) => setProjectDialog((d) => ({ ...d, form: { ...d.form, name: e.target.value } }))} className="h-12" data-testid="edit-project-name" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Client name">
                <Input value={projectDialog.form.client_name} onChange={(e) => setProjectDialog((d) => ({ ...d, form: { ...d.form, client_name: e.target.value } }))} className="h-12" data-testid="edit-project-client" />
              </Field>
              <Field label="Original estimate (AUD)">
                <Input type="number" step="1" min="0" value={projectDialog.form.original_estimate} onChange={(e) => setProjectDialog((d) => ({ ...d, form: { ...d.form, original_estimate: e.target.value } }))} className="h-12" data-testid="edit-project-estimate" />
              </Field>
            </div>
            <Field label="Site address">
              <Input value={projectDialog.form.address} onChange={(e) => setProjectDialog((d) => ({ ...d, form: { ...d.form, address: e.target.value } }))} className="h-12" data-testid="edit-project-address" />
            </Field>
            <Field label="Notes">
              <Textarea value={projectDialog.form.notes} onChange={(e) => setProjectDialog((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))} rows={3} data-testid="edit-project-notes" />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="h-12 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-save-edit-project">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Trade dialog (new or edit) */}
      <Dialog open={tradeDialog.open} onOpenChange={(o) => setTradeDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{tradeDialog.editingId ? "Edit trade" : "New trade"}</DialogTitle></DialogHeader>
          <form onSubmit={submitTrade} className="space-y-4" data-testid={tradeDialog.editingId ? "edit-trade-form" : "new-trade-form"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Trade / supplier name" required>
                <Input required value={tradeDialog.form.name} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, name: e.target.value } }))} className="h-12" data-testid="trade-name" />
              </Field>
              <Field label="Category">
                <Select value={tradeDialog.form.category} onValueChange={(v) => setTradeDialog((d) => ({ ...d, form: { ...d.form, category: v } }))}>
                  <SelectTrigger className="h-12" data-testid="trade-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{TRADE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Supplier / contact">
              <Input value={tradeDialog.form.supplier} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, supplier: e.target.value } }))} className="h-12" data-testid="trade-supplier" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Original estimate">
                <Input type="number" min="0" value={tradeDialog.form.original_estimate} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, original_estimate: e.target.value } }))} className="h-12" data-testid="trade-original" />
              </Field>
              <Field label="Updated estimate">
                <Input type="number" min="0" value={tradeDialog.form.updated_estimate} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, updated_estimate: e.target.value } }))} className="h-12" placeholder="Optional" data-testid="trade-updated" />
              </Field>
              <Field label="Quoted amount">
                <Input type="number" min="0" value={tradeDialog.form.quoted_amount} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, quoted_amount: e.target.value } }))} className="h-12" placeholder="Optional" data-testid="trade-quoted" />
              </Field>
              <Field label="Approved variations">
                <Input type="number" value={tradeDialog.form.approved_variations} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, approved_variations: e.target.value } }))} className="h-12" data-testid="trade-variations" />
              </Field>
            </div>
            <Field label="Status">
              <Select value={tradeDialog.form.status} onValueChange={(v) => setTradeDialog((d) => ({ ...d, form: { ...d.form, status: v } }))}>
                <SelectTrigger className="h-12" data-testid="trade-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_quoted">Not Quoted</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="part_paid">Part Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="over_budget">Over Budget</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description / notes">
              <Textarea value={tradeDialog.form.notes} onChange={(e) => setTradeDialog((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))} rows={3} data-testid="trade-notes" />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="h-12 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-save-trade">
                {saving ? "Saving…" : tradeDialog.editingId ? "Save changes" : "Add trade"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon, label, value, tone = "neutral" }) {
  const toneCls = { neutral: "text-slate-900", paid: "text-status-paid-text", part: "text-status-part-text", danger: "text-status-danger-text" }[tone];
  const iconBg = {
    neutral: "bg-slate-100 text-slate-600",
    paid: "bg-status-paid-bg text-status-paid-text",
    part: "bg-status-part-bg text-status-part-text",
    danger: "bg-status-danger-bg text-status-danger-text",
  }[tone];
  return (
    <Card className="p-4 border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${iconBg} [&>svg]:h-4 [&>svg]:w-4`}>{icon}</div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">{label}</div>
          <div className={`font-display font-bold text-xl md:text-2xl mt-1 ${toneCls}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function TradeRow({ t, onEdit, onDelete }) {
  const meta = statusMeta(t.status);
  const f = t.finance || {};
  const over = f.over_budget;
  return (
    <Card className="border border-border shadow-sm p-4 hover:shadow-md transition-transform duration-200" data-testid={`trade-row-${t.id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`} data-testid={`trade-status-${t.id}`}>{meta.label}</span>
            {t.category && <span className="text-xs text-slate-500 uppercase tracking-wider">{t.category}</span>}
            {over && <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-status-danger-bg text-status-danger-text border-status-danger-border">Over budget</span>}
          </div>
          <Link to={`/trades/${t.id}`} data-testid={`trade-link-${t.id}`}>
            <h3 className="font-display text-lg font-semibold text-slate-900 mt-1.5 hover:text-brandblue transition-colors">
              {t.name}
            </h3>
          </Link>
          {t.supplier && <div className="text-xs text-slate-500">{t.supplier}</div>}
        </div>

        <div className="grid grid-cols-4 gap-4 md:gap-6 md:min-w-[440px]">
          <MiniStat label="Est. final" value={fmtMoney(f.estimated_final)} />
          <MiniStat label="Paid" value={fmtMoney(f.total_paid)} tone="paid" />
          <MiniStat label="Remaining" value={fmtMoney(f.remaining_balance)} tone={f.remaining_balance > 0 ? "part" : "paid"} />
          <div className="flex items-center justify-end gap-1">
            <Link to={`/trades/${t.id}`}>
              <Button variant="outline" size="sm" className="h-9" data-testid={`btn-view-trade-${t.id}`}>Open</Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400" data-testid={`btn-trade-more-${t.id}`}><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit} data-testid={`menu-edit-trade-${t.id}`}><Pencil className="h-4 w-4" /> Edit trade</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-status-danger-text" data-testid={`menu-delete-trade-${t.id}`}>
                  <Trash2 className="h-4 w-4" /> Delete trade
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </Card>
  );
}

function MiniStat({ label, value, tone = "neutral" }) {
  const c = { neutral: "text-slate-900", paid: "text-status-paid-text", part: "text-status-part-text", danger: "text-status-danger-text" }[tone];
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display font-semibold text-sm md:text-base ${c}`}>{value}</div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-status-danger-text">*</span>}
      </Label>
      {children}
    </div>
  );
}
